import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Palette, RotateCcw } from 'lucide-react';
import { HslColorPicker } from 'react-colorful';

interface ThemeCustomizerProps {
  getSetting: (key: string) => string;
  updateSetting: (key: string, value: string) => Promise<boolean>;
}

export const ThemeCustomizer = ({ getSetting, updateSetting }: ThemeCustomizerProps) => {
  const [hue, setHue] = useState(142);
  const [saturation, setSaturation] = useState(76);
  const [lightness, setLightness] = useState(30);
  const [customEnabled, setCustomEnabled] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const { toast } = useToast();

  // Load current values
  useEffect(() => {
    setHue(parseInt(getSetting('theme_primary_hue') || '142'));
    setSaturation(parseInt(getSetting('theme_primary_saturation') || '76'));
    setLightness(parseInt(getSetting('theme_primary_lightness') || '30'));
    setCustomEnabled(getSetting('theme_custom_enabled') === 'true');
  }, [getSetting]);

  const presetColors = [
    { name: 'Կանաչ (Դեֆաուլտ)', h: 142, s: 76, l: 30 },
    { name: 'Կապույտ', h: 217, s: 91, l: 60 },
    { name: 'Մանուշակագույն', h: 262, s: 52, l: 47 },
    { name: 'Կարմիր', h: 0, s: 84, l: 60 },
    { name: 'Նարնջագույն', h: 25, s: 95, l: 53 },
    { name: 'Վարդագույն', h: 330, s: 81, l: 60 },
  ];

  const handleApply = async () => {
    setIsUpdating(true);
    try {
      const results = await Promise.all([
        updateSetting('theme_primary_hue', hue.toString()),
        updateSetting('theme_primary_saturation', saturation.toString()),
        updateSetting('theme_primary_lightness', lightness.toString()),
        updateSetting('theme_custom_enabled', customEnabled.toString()),
      ]);

      if (results.every(r => r)) {
        toast({
          title: "Հաջողություն",
          description: "Թեմայի գույները պահպանվել են",
        });
      } else {
        throw new Error('Failed to save some settings');
      }
    } catch (error) {
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց պահպանել կարգավորումները",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleReset = async () => {
    setIsUpdating(true);
    try {
      setHue(142);
      setSaturation(76);
      setLightness(30);
      setCustomEnabled(false);
      
      const results = await Promise.all([
        updateSetting('theme_primary_hue', '142'),
        updateSetting('theme_primary_saturation', '76'),
        updateSetting('theme_primary_lightness', '30'),
        updateSetting('theme_custom_enabled', 'false'),
      ]);

      if (results.every(r => r)) {
        toast({
          title: "Հաջողություն",
          description: "Թեման վերադարձվել է դեֆաուլտ տարբերակին",
        });
      }
    } catch (error) {
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց վերականգնել դեֆաուլտ կարգավորումները",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const applyPreset = (preset: typeof presetColors[0]) => {
    setHue(preset.h);
    setSaturation(preset.s);
    setLightness(preset.l);
  };

  const currentColor = `hsl(${hue}, ${saturation}%, ${lightness}%)`;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Palette className="h-5 w-5" />
          Թեմայի գունային կառավարում
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Enable/Disable Custom Theme */}
        <div className="flex items-center justify-between">
          <Label htmlFor="custom-theme-toggle">Միացնել custom թեման</Label>
          <Switch
            id="custom-theme-toggle"
            checked={customEnabled}
            onCheckedChange={setCustomEnabled}
          />
        </div>

        {customEnabled && (
          <>
            {/* HSL Sliders */}
            <div className="space-y-4">
              <div>
                <Label>Hue (Գունային երանգ): {hue}°</Label>
                <Slider
                  value={[hue]}
                  onValueChange={([value]) => setHue(value)}
                  min={0}
                  max={360}
                  step={1}
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Saturation (Հագեցվածություն): {saturation}%</Label>
                <Slider
                  value={[saturation]}
                  onValueChange={([value]) => setSaturation(value)}
                  min={0}
                  max={100}
                  step={1}
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Lightness (Պայծառություն): {lightness}%</Label>
                <Slider
                  value={[lightness]}
                  onValueChange={([value]) => setLightness(value)}
                  min={0}
                  max={100}
                  step={1}
                  className="mt-2"
                />
              </div>
            </div>

            {/* Color Preview */}
            <div className="space-y-2">
              <Label>Ընթացիկ գույն:</Label>
              <div className="flex items-center gap-4">
                <div 
                  className="w-24 h-24 rounded-lg border-2 border-border shadow-md"
                  style={{ backgroundColor: currentColor }}
                />
                <div className="space-y-1">
                  <p className="text-sm">{currentColor}</p>
                  <p className="text-sm text-muted-foreground">
                    H: {hue}° S: {saturation}% L: {lightness}%
                  </p>
                </div>
              </div>
            </div>

            {/* Preset Colors */}
            <div className="space-y-2">
              <Label>Նախապես սահմանված գույներ:</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {presetColors.map((preset) => (
                  <Button
                    key={preset.name}
                    variant="outline"
                    onClick={() => applyPreset(preset)}
                    className="h-auto py-3 flex flex-col items-start gap-2"
                  >
                    <div 
                      className="w-full h-8 rounded"
                      style={{ backgroundColor: `hsl(${preset.h}, ${preset.s}%, ${preset.l}%)` }}
                    />
                    <span className="text-xs">{preset.name}</span>
                  </Button>
                ))}
              </div>
            </div>

            {/* Advanced Color Picker */}
            <div className="space-y-2">
              <Label>Ընդլայնված գունաընտրիչ:</Label>
              <div className="flex justify-center p-4 bg-muted rounded-lg">
                <HslColorPicker
                  color={{ h: hue, s: saturation, l: lightness }}
                  onChange={(color) => {
                    setHue(Math.round(color.h));
                    setSaturation(Math.round(color.s));
                    setLightness(Math.round(color.l));
                  }}
                />
              </div>
            </div>
          </>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button 
            onClick={handleApply} 
            disabled={isUpdating}
            className="flex-1"
          >
            {isUpdating ? 'Պահպանվում է...' : 'Կիրառել գույները'}
          </Button>
          <Button 
            onClick={handleReset} 
            disabled={isUpdating}
            variant="outline"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Դեֆաուլտ
          </Button>
        </div>

        {/* Info Text */}
        {customEnabled && (
          <p className="text-xs text-muted-foreground">
            Գույները կիրառվում են ռեալ ժամանակում և ազդում են ամբողջ կայքի թեմայի վրա։
          </p>
        )}
      </CardContent>
    </Card>
  );
};
